
import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { findBlogPostBySlug, getNextAndPreviousPost } from "@/utils/blog";
import BlogHeader from "@/components/blog/BlogHeader";
import BlogContent from "@/components/blog/BlogContent";
import BlogAuthor from "@/components/blog/BlogAuthor";
import BlogMeta from "@/components/blog/BlogMeta";
import BlogBreadcrumb from "@/components/blog/BlogBreadcrumb";
import BlogPostNavigation from "@/components/blog/BlogPostNavigation";
import BlogNotFound from "@/components/blog/BlogNotFound";
import BlogPostFooter from "@/components/blog/BlogPostFooter";
import BlogPostSEO from "@/components/blog/BlogPostSEO";
import SEOMetadata from "@/components/SEOMetadata";
import { breadcrumbStructuredData } from "@/data/structuredData";
import { Helmet } from "react-helmet";
import CTASection from "@/components/CTASection";
import RelatedArticles from "@/components/blog/RelatedArticles";
import YouTubeLink from "@/components/YouTubeLink";
import { useBlogAnalytics } from "@/hooks/useBlogAnalytics";
import { logger } from "@/utils/logger";

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  
  // Find the current post by slug
  const post = findBlogPostBySlug(slug || "");
  
  // Initialize blog analytics
  const { trackTagClick, trackRelatedArticleClick } = useBlogAnalytics({
    trackBlogViews: true,
    trackTagPerformance: true,
    trackScrollDepth: true
  }, {
    slug: post?.slug,
    category: post?.category,
    tags: post?.tags,
    readingTime: post?.readingTime
  });
  
  useEffect(() => {
    // Scroll to top when blog post loads
    window.scrollTo(0, 0);
    
    // Enhanced logging for debugging
    logger.log("BlogPost component rendered for slug:", slug);
    if (post) {
      logger.log("Post found:", post);
      logger.log("Content type:", typeof post.content);
      logger.log("Content available:", !!post.content);
      
      // Log more specific details based on content type
      if (typeof post.content === 'string') {
        logger.log("String content length:", post.content.length);
        logger.log("String content preview:", post.content.substring(0, 100) + '...');
      } else if (post.content && typeof post.content === 'object') {
        logger.log("Content structure:", 
          'sections' in post.content ? 
            `Object with ${(post.content as any).sections?.length || 0} sections` : 
            'Unknown object structure'
        );
        
        if ('sections' in post.content) {
          logger.log("Sections sample:", 
            (post.content as any).sections.slice(0, 2).map((s: any) => ({
              id: s.id, 
              title: s.title,
              contentPreview: s.content.substring(0, 50) + '...'
            }))
          );
        }
      }
    } else {
      logger.warn("No post found with slug:", slug);
    }
  }, [slug, post]);
  
  // Handle case where post is not found
  if (!post) {
    return <BlogNotFound />;
  }
  
  // Get next and previous posts
  const { nextPost, previousPost } = getNextAndPreviousPost(slug || "");

  // Get current URL for sharing
  const currentUrl = window.location.href;

  // Determine article-specific about topics for Schema.org
  const getAboutTopics = (): string[] => {
    const topicMap: Record<string, string[]> = {
      'fuehrung-2026-haltung-empathie': ['emotionaleIntelligenz', 'psychologischeSicherheit', 'koRegulation', 'selbstbestimmungstheorie', 'fuehrungskompetenz', 'selbstfuehrung'],
      'outdoor-coaching': ['outdoorCoaching', 'koerperintelligenz', 'achtsamkeit', 'resilienz'],
      'koerper-fuehrung': ['koerperintelligenz', 'neuroplastizitaet', 'resilienz'],
      'etf-mythos': ['verhaltungsoekonomie', 'finanzcoaching'],
      'neuroplastizitaet-epigenetik': ['neuroplastizitaet', 'koerperintelligenz', 'resilienz'],
      'fromm-haben-sein': ['selbstfuehrung', 'achtsamkeit'],
      'mythos-positives-denken': ['resilienz', 'achtsamkeit'],
      'betriebssystem-v5-2': ['selbstfuehrung', 'resilienz', 'achtsamkeit'],
      'digitaler-humanismus-kommunikation': ['emotionaleIntelligenz'],
      'kostolany-finanzweisheit': ['finanzcoaching', 'verhaltungsoekonomie'],
      'finanzcoaching-vs-finanzberatung': ['finanzcoaching', 'verhaltungsoekonomie', 'selbstbestimmungstheorie', 'hochsensibilitaet'],
      'gesundes-verhaeltnis-zu-geld': ['finanzcoaching', 'verhaltungsoekonomie'],
      'social-media-emotionen': ['emotionaleIntelligenz', 'resilienz'],
      'ki-finanzielle-emotionale-unabhaengigkeit': ['finanzcoaching', 'resilienz'],
      'coaching-als-lebensphilosophie': ['selbstfuehrung', 'achtsamkeit', 'resilienz'],
      'hochsensible-fuehrung': ['hochsensibilitaet', 'psychologischeSicherheit', 'emotionaleIntelligenz', 'fuehrungskompetenz', 'selbstfuehrung'],
      'souveraenitaet-komplexitaet-katalysator-methode': ['aktivierungsenergie', 'eisbergmodell', 'entscheidungsmuedigkeit', 'polyvagalTheorie', 'systemtheorie', 'emotionaleIntelligenz', 'katalysatorFuehrung'],
    };
    return topicMap[post.slug] || [];
  };

  // Determine if CTA should be shown
  const showCTA = post.slug === "outdoor-coaching";

  // Extract plain text from content for articleBody schema
  const getArticleBody = (): string => {
    if (typeof post.content === 'string') {
      // Strip HTML tags for plain text
      return post.content.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().substring(0, 5000);
    } else if (post.content && 'sections' in post.content) {
      // Extract text from sections
      return post.content.sections
        .map(s => s.content.replace(/<[^>]*>/g, ' '))
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim()
        .substring(0, 5000);
    }
    return post.excerpt;
  };

  return (
    <main className="min-h-screen relative" tabIndex={-1}>
      <SEOMetadata
        metaTitle={post.metaTitle ? `${post.metaTitle} | Jörg Hörmann Blog` : `${post.title} | Jörg Hörmann Blog`}
        metaDescription={post.metaDescription || post.excerpt}
        pageName={post.title}
        primaryLink={`/blog/${post.slug}`}
        title={post.title}
        description={post.metaDescription || post.excerpt}
        imageUrl={post.imageUrl}
        canonicalUrl={`https://joerg-hoermann.de/blog/${post.slug}`}
        articleSchema={false}
        datePublished={post.date}
        dateModified={post.lastUpdated}
        author={post.author}
        category={post.category}
      />
      <BlogPostSEO 
        title={post.metaTitle || post.title} 
        excerpt={post.metaDescription || post.excerpt} 
        imageUrl={post.imageUrl}
        slug={post.slug}
        author={post.author}
        datePublished={post.date}
        dateModified={post.lastUpdated}
        articleBody={getArticleBody()}
        category={post.category}
        tags={post.tags}
        aboutTopics={getAboutTopics()}
      />
      
      {/* Breadcrumb structured data with real article title */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbStructuredData([
            { name: "Startseite", url: "https://joerg-hoermann.de" },
            { name: "Blog", url: "https://joerg-hoermann.de/blog" },
            { name: post.title, url: `https://joerg-hoermann.de/blog/${post.slug}` }
          ]))}
        </script>
      </Helmet>

      <article className="bg-white pt-8 md:pt-12 pb-16 relative z-1">
        <div className="container px-4 mx-auto font-normal">
          <div className="max-w-3xl mx-auto">
            {/* AEO: Speakable summary for answer engines */}
            <p className="sr-only" data-speakable="summary">
              {post.metaDescription || post.excerpt}
            </p>

            {/* Breadcrumb navigation */}
            <BlogBreadcrumb title={post.title} />

            {/* Blog header - title, meta, featured image */}
            <BlogHeader 
              title={post.title}
              subtitle={post.subtitle}
              date={post.date} 
              category={post.category}
              coverImage={post.imageUrl}
              imageAlt={post.title}
            />
            
            {/* Blog meta information - date, author */}
            <BlogMeta 
              date={post.date}
              lastUpdated={post.lastUpdated}
              author={post.author || "Jörg Hörmann"}
              tags={post.tags}
            />
            
            {/* Blog content with clear positioning */}
            <div className="mt-8 relative z-10">
              {post.content ? (
                <BlogContent contentToRender={post.content} slug={post.slug} />
              ) : (
                <div className="text-center py-12">
                  <p className="text-joerg-600">Der Inhalt dieses Beitrags konnte leider nicht geladen werden.</p>
                </div>
              )}
            </div>
            
            {/* Article bottom section - Only show author for non-outdoor-coaching posts */}
            {post.slug !== "outdoor-coaching" && <BlogAuthor />}
            
            {/* YouTube link for musik-und-emotionen article */}
            {post.slug === 'musik-und-emotionen' && (
              <div className="mt-6 pt-4 border-t border-joerg-200/50 text-center">
                <a 
                  href="https://youtu.be/4vikGJUZi0c" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sage-700 hover:text-primary transition-colors text-sm underline-offset-4 hover:underline"
                >
                  Zum Video-Impuls auf YouTube
                  <span aria-hidden="true">→</span>
                </a>
              </div>
            )}
            
            {/* YouTube link for KI article */}
            {post.slug === 'ki-finanzielle-emotionale-unabhaengigkeit' && (
              <div className="mt-8">
                <YouTubeLink 
                  youtubeId="esCSpbDPJik" 
                  title="Dario Amodei Interview auf YouTube"
                  description="Vertiefende Einblicke und spannende Impulse für deine persönliche Auseinandersetzung mit KI."
                  showThumbnail={false}
                />
              </div>
            )}
            
            {/* Share buttons and back to blog link */}
            <BlogPostFooter title={post.title} url={currentUrl} />
            
            {/* Related Articles */}
            {post && post.tags && (
              <RelatedArticles 
                currentPost={post}
              />
            )}
            
            {/* Previous/Next navigation */}
            <BlogPostNavigation 
              previousPost={previousPost} 
              nextPost={nextPost} 
            />
          </div>
        </div>
      </article>
      
      {/* Optional CTA Section */}
      {showCTA && (
        <CTASection 
          title="Bereit für ein Outdoor-Coaching Erlebnis?" 
          description="Nutzen Sie die Kraft der Natur für Ihre finanzielle und persönliche Entwicklung."
          primaryCTA={{
            text: "Kostenfreies Erstgespräch vereinbaren",
            link: "https://zeeg.me/joerghoermann",
            isExternal: true
          }}
          backgroundClass="bg-gradient-to-br from-[#EDF5E9] to-[#E8F1FB] text-joerg-800"
        />
      )}
    </main>
  );
};

export default BlogPost;
